// Settings › Branding — the brand sheet.
//
// One continuous sheet with three zones rather than three rows of filenames. The old rows
// showed a 20px square thumbnail and the file's name, which is the least useful fact
// available: nobody identifies a logo by remembering what they called the file. Each zone now
// previews its asset at a size that answers a real question.
//
// The logo zone shows BOTH grounds side by side, which is the whole argument for this layout: a
// logo drawn for a white page goes onto the dark sidebar unchanged, and until now the only way
// to find that out was to look at the app. The dark panel previews the chosen treatment through
// the same resolver app chrome uses, filter included, so picking "invert" shows you immediately
// whether it works for your mark instead of asking you to guess and then go look.
//
// The favicon deliberately is NOT just scaled up. It ships at 16-32px, so it is shown at those
// sizes inside a tab strip, with the artwork enlarged alongside for identification only.

import { useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SEOConfig, ThemeConfig } from '@/types/theme';
import {
  LOGO_INVERT_FILTER,
  countSeoFieldsSet,
  getAdaptiveSidebarColor,
  normalizeLogoDarkTreatment,
  resolveAppLogo,
  type LogoDarkTreatment,
} from './brandingHelpers';

type AssetKind = 'logo' | 'logoDark' | 'favicon';

interface CompanyBrandPanelProps {
  currentTheme: ThemeConfig;
  logoUrl: string | null;
  /** The uploaded dark-mode variant. Only used when the treatment is 'custom'. */
  logoDarkUrl: string | null;
  /** How the logo is handled in dark mode. Undefined reads as 'same'. */
  logoDarkTreatment: string | null | undefined;
  faviconUrl: string | null;
  seo: SEOConfig;
  canEdit: boolean;
  sidebarColorOverride: string | null | undefined;
  onUploadAsset: (file: File) => Promise<string>;
  onLogoChange: (url: string) => Promise<void>;
  onLogoDarkChange: (url: string) => Promise<void>;
  onTreatmentChange: (treatment: LogoDarkTreatment) => Promise<void>;
  onFaviconChange: (url: string) => Promise<void>;
  onSidebarColorChange: (color: string) => Promise<void>;
  onEditSeo: () => void;
}

/** Transparency checkerboard, so a transparent PNG reads as transparent rather than white. */
const CHECKER: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, rgba(0,0,0,.06) 25%, transparent 25%, transparent 75%, rgba(0,0,0,.06) 75%), linear-gradient(45deg, rgba(0,0,0,.06) 25%, transparent 25%, transparent 75%, rgba(0,0,0,.06) 75%)',
  backgroundSize: '14px 14px',
  backgroundPosition: '0 0, 7px 7px',
  backgroundColor: '#FFFFFF',
};

function ZoneHeading({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h4 className="font-mono text-[0.65rem] uppercase tracking-wider text-muted-foreground">{title}</h4>
      {action}
    </div>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'muted'; children: React.ReactNode }) {
  const tones = {
    ok: 'border-primary/30 bg-primary/10 text-primary',
    warn: 'border-amber-300 bg-amber-50 text-amber-700',
    muted: 'border-border bg-muted/50 text-muted-foreground',
  } as const;
  return (
    <span
      className={`whitespace-nowrap rounded-sm border px-1.5 py-0.5 font-mono text-[0.55rem] uppercase tracking-wider ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function CompanyBrandPanel({
  currentTheme,
  logoUrl,
  logoDarkUrl,
  logoDarkTreatment,
  faviconUrl,
  seo,
  canEdit,
  sidebarColorOverride,
  onUploadAsset,
  onLogoChange,
  onLogoDarkChange,
  onTreatmentChange,
  onFaviconChange,
  onSidebarColorChange,
  onEditSeo,
}: CompanyBrandPanelProps) {
  const inputs = {
    logo: useRef<HTMLInputElement>(null),
    logoDark: useRef<HTMLInputElement>(null),
    favicon: useRef<HTMLInputElement>(null),
  };
  const [uploading, setUploading] = useState<AssetKind | null>(null);

  const adaptiveColor = getAdaptiveSidebarColor(
    currentTheme.colors.primary,
    currentTheme.colors.background,
  );
  const activeSidebar = currentTheme.colors.sidebarColor;
  const usingCustomSidebar = currentTheme.isCustom
    ? activeSidebar !== '#000000' && activeSidebar !== adaptiveColor
    : !!sidebarColorOverride && sidebarColorOverride !== '#000000' && sidebarColorOverride !== adaptiveColor;

  const treatment = normalizeLogoDarkTreatment(logoDarkTreatment);

  // Resolved through the same function app chrome uses, so the panel below cannot disagree
  // with what the sidebar actually renders.
  const darkPreview = resolveAppLogo(logoUrl, logoDarkUrl, treatment, true);

  const handleFile = async (kind: AssetKind, file: File | undefined) => {
    if (!file) return;
    setUploading(kind);
    try {
      const url = await onUploadAsset(file);
      if (kind === 'logo') await onLogoChange(url);
      else if (kind === 'logoDark') await onLogoDarkChange(url);
      else await onFaviconChange(url);
    } finally {
      setUploading(null);
    }
  };

  const ReplaceButton = ({ kind, label = 'Replace' }: { kind: AssetKind; label?: string }) =>
    canEdit ? (
      <Button
        size="sm"
        variant="outline"
        className="h-6 shrink-0 px-2 text-xs"
        onClick={() => inputs[kind].current?.click()}
        disabled={uploading !== null}
      >
        {uploading === kind ? <Loader2 className="h-3 w-3 animate-spin" /> : label}
      </Button>
    ) : null;

  const titleLen = seo.pageTitle?.length ?? 0;
  const descLen = seo.metaDescription?.length ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assets</CardTitle>
          <CardDescription>
            What people see in the app, in a browser tab, and on a link someone shares.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-md border border-border">
            {/* ---- Logo: proofed on both grounds it has to survive ---- */}
            <div className="p-3">
              <ZoneHeading title="Logo" />
              <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]">
                <div className="overflow-hidden rounded-md border border-border">
                  <div className="flex h-24 items-center justify-center p-3" style={CHECKER}>
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Upload className="h-3.5 w-3.5" /> No logo set
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-2 py-1.5">
                    <span className="font-mono text-[0.55rem] uppercase tracking-wider text-muted-foreground">
                      On light
                    </span>
                    <ReplaceButton kind="logo" />
                  </div>
                </div>

                <div className="overflow-hidden rounded-md border border-border">
                  <div
                    className="flex h-24 items-center justify-center p-3"
                    style={{ backgroundColor: activeSidebar }}
                  >
                    {darkPreview.url ? (
                      <img
                        src={darkPreview.url}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                        // The preview runs the real filter, so what you see here is exactly
                        // what the sidebar will render.
                        style={darkPreview.invert ? { filter: LOGO_INVERT_FILTER } : undefined}
                      />
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs text-white/60">
                        <Upload className="h-3.5 w-3.5" /> No logo set
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-2 py-1.5">
                    <span className="font-mono text-[0.55rem] uppercase tracking-wider text-muted-foreground">
                      In dark mode
                    </span>
                    {treatment === 'custom' && (
                      <span className="flex items-center gap-1">
                        <ReplaceButton kind="logoDark" label={logoDarkUrl ? 'Replace' : 'Upload'} />
                        {canEdit && logoDarkUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() => void onLogoDarkChange('')}
                            disabled={uploading !== null}
                          >
                            Remove
                          </Button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/*
                Three treatments rather than an automatic guess. Inverting works genuinely well
                for a solid-color mark and mangles anything with more than one color, which is
                why it used to be a bug and is now a choice: the panel above previews the actual
                result on the actual sidebar color before it is saved.
              */}
              <fieldset className="mt-3" disabled={!canEdit}>
                <legend className="mb-1.5 text-xs font-medium text-foreground">In dark mode</legend>
                <div className="flex flex-col gap-1.5">
                  {(
                    [
                      // Each hint names the kind of logo the option suits, so the three read as
                      // one decision rather than three unrelated settings.
                      {
                        value: 'same',
                        label: 'Use the same logo',
                        hint: 'Best if your logo already works on light and dark.',
                      },
                      {
                        value: 'invert',
                        label: 'Invert the colors',
                        hint: 'Works well for a solid-color mark. Check the preview.',
                      },
                      {
                        value: 'custom',
                        label: 'Upload a dark logo',
                        hint: 'Best for a detailed or multi-color mark.',
                      },
                    ] as const
                  ).map((option) => (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-2 text-xs ${
                        canEdit ? '' : 'cursor-not-allowed opacity-60'
                      }`}
                    >
                      <input
                        type="radio"
                        name="logo-dark-treatment"
                        value={option.value}
                        checked={treatment === option.value}
                        onChange={() => void onTreatmentChange(option.value)}
                        disabled={!canEdit}
                        className="mt-0.5 text-primary focus:ring-primary"
                      />
                      <span>
                        <span className="font-medium text-foreground">{option.label}</span>
                        <span className="text-muted-foreground"> {option.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {treatment === 'custom' && logoDarkUrl === null && (
                <p className="mt-2 text-xs text-amber-700">
                  No dark logo uploaded yet, so the light one is still being used.
                </p>
              )}
            </div>

            {/* ---- Favicon: true size, in something recognisable ---- */}
            <div className="border-t border-border p-3">
              <ZoneHeading title="Favicon" action={<ReplaceButton kind="favicon" />} />
              {faviconUrl ? (
                <div className="flex flex-wrap items-end gap-5">
                  <div className="flex items-end gap-3">
                    {[
                      { size: 48, label: 'Artwork' },
                      { size: 32, label: '32px' },
                      { size: 16, label: '16px' },
                    ].map(({ size, label }) => (
                      <div key={label} className="flex flex-col items-center gap-1">
                        <img
                          src={faviconUrl}
                          alt=""
                          style={{ width: size, height: size }}
                          className="rounded-[22%] object-contain"
                        />
                        <span className="font-mono text-[0.52rem] text-muted-foreground">{label}</span>
                      </div>
                    ))}
                  </div>

                  {/* A tab strip, because 16px in isolation tells you nothing. */}
                  <div className="flex max-w-[15rem] items-end gap-1 rounded-t-md bg-muted px-1 pt-1">
                    <span className="flex items-center gap-1.5 truncate rounded-t-md bg-background px-2 py-1 text-[0.68rem] text-foreground">
                      <img src={faviconUrl} alt="" className="h-3.5 w-3.5 shrink-0 object-contain" />
                      {seo.pageTitle || 'Joules'}
                    </span>
                    <span className="hidden shrink-0 rounded-t-md px-2 py-1 text-[0.68rem] text-muted-foreground sm:inline">
                      Docs
                    </span>
                  </div>
                </div>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Upload className="h-3.5 w-3.5" /> No favicon set. Browsers will show their default.
                </span>
              )}
            </div>

            {/* ---- SEO & Social ---- */}
            <div className="border-t border-border p-3">
              <ZoneHeading
                title="SEO & Social"
                action={
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onEditSeo}>
                    {canEdit ? 'Edit' : 'View'}
                  </Button>
                }
              />
              {/* Rendered as the search result it becomes, not as a field count. The blue/green
                  here are Google's own result colors, so they are literals on purpose rather
                  than theme tokens. */}
              <div className="flex max-w-[30rem] flex-col gap-0.5">
                <span className="truncate text-[0.7rem] text-muted-foreground">
                  {seo.canonicalUrl || 'app.joulescrm.com'}
                </span>
                <span className="text-sm font-semibold leading-tight text-[#1A0DAB] dark:text-[#8AB4F8]">
                  {seo.pageTitle || 'No page title set'}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {seo.metaDescription || 'No description set. Search engines will invent one.'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Pill tone={titleLen === 0 ? 'warn' : titleLen > 60 ? 'warn' : 'ok'}>
                  Title {titleLen}/60
                </Pill>
                <Pill tone={descLen === 0 ? 'warn' : descLen > 160 ? 'warn' : 'ok'}>
                  Description {descLen}/160
                </Pill>
                {seo.ogImage?.trim() ? (
                  <Pill tone="ok">Share image set</Pill>
                ) : (
                  <Pill tone="warn">No share image</Pill>
                )}
                <Pill tone="muted">{countSeoFieldsSet(seo)} of 12 fields</Pill>
              </div>
            </div>
          </div>

          {(['logo', 'logoDark', 'favicon'] as const).map((kind) => (
            <input
              key={kind}
              ref={inputs[kind]}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handleFile(kind, e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sidebar</CardTitle>
          <CardDescription>
            Keep the sidebar dark, let it follow the scheme, or pin a specific color.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="sidebar-black"
              name="sidebar-color"
              checked={
                currentTheme.isCustom ? activeSidebar === '#000000' : sidebarColorOverride === '#000000'
              }
              onChange={() => void onSidebarColorChange('#000000')}
              disabled={!canEdit}
              className="text-primary focus:ring-primary"
            />
            <label htmlFor="sidebar-black" className="flex items-center gap-2 text-sm">
              <span className="h-5 w-5 rounded border-2 border-border" style={{ backgroundColor: '#000000' }} />
              Black
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="sidebar-theme"
              name="sidebar-color"
              checked={
                currentTheme.isCustom
                  ? activeSidebar === adaptiveColor
                  : !sidebarColorOverride || sidebarColorOverride === adaptiveColor
              }
              onChange={() => void onSidebarColorChange(adaptiveColor)}
              disabled={!canEdit}
              className="text-primary focus:ring-primary"
            />
            <label htmlFor="sidebar-theme" className="flex items-center gap-2 text-sm">
              <span
                className="h-5 w-5 rounded border-2 border-border"
                style={{ backgroundColor: adaptiveColor }}
              />
              Follow the scheme
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="use-custom-sidebar"
              checked={usingCustomSidebar}
              onChange={(e) => {
                if (e.target.checked) {
                  const next =
                    activeSidebar === '#000000' || activeSidebar === adaptiveColor ? '#3b82f6' : activeSidebar;
                  void onSidebarColorChange(next);
                } else {
                  void onSidebarColorChange(adaptiveColor);
                }
              }}
              disabled={!canEdit}
              className="text-primary focus:ring-primary"
            />
            <label htmlFor="use-custom-sidebar" className="text-sm font-medium">
              Use a specific color
            </label>
          </div>

          <div className={`space-y-2 transition-opacity ${usingCustomSidebar ? 'opacity-100' : 'opacity-50'}`}>
            <Label htmlFor="custom-sidebar-color">Sidebar color</Label>
            <div className="flex gap-2">
              <Input
                id="custom-sidebar-color"
                type="color"
                value={activeSidebar}
                onChange={(e) => void onSidebarColorChange(e.target.value)}
                disabled={!canEdit || !usingCustomSidebar}
                className="h-10 w-20"
              />
              <Input
                type="text"
                value={activeSidebar}
                onChange={(e) => void onSidebarColorChange(e.target.value)}
                disabled={!canEdit || !usingCustomSidebar}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
